import React, {ReactNode} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {EndlessList, mapListConfig} from 'components/ui-kit/EndlessList2.0';
import {
    listState$,
    sortingsChanged,
    filtersChanged,
    filtersSubmit,
    filtersReset,
    Gate,
    pageIncremented,
} from 'models/orders/grid/list';
import {OrdersGridRawRow, OrdersGridFormattedRow, OrdersAccessors, GridFieldConfig} from 'types';
import {formatNumber} from 'utils/number';

import {ListRow, FlexRowElement, Text, RowElement} from './styled';

interface Props {
    configFunc: (t: TFunction) => GridFieldConfig[];
    dataMapper: (data: OrdersGridRawRow[] | undefined, t: TFunction) => OrdersGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const OrdersGridList = ({configFunc, dataMapper, headerControls, exportConfig}: Props) => {
    const {t} = useTranslation();
    const {data, totalCount, sortings, filters, showConfigBlock} = useStore(listState$);
    const mappedData = React.useMemo(() => dataMapper(data, t), [dataMapper, data, t]);

    return (
        <>
            <Gate />
            <EndlessList<OrdersGridFormattedRow>
                rows={mappedData}
                totalRowsCount={totalCount}
                hasMore={data.length < totalCount}
                headerControls={headerControls}
                renderRow={(row: OrdersGridFormattedRow) => (
                    <ListRow>
                        <FlexRowElement>
                            <Text>{row[OrdersAccessors.createdDate]}</Text>
                            <Text isGray>{row[OrdersAccessors.status]}</Text>
                        </FlexRowElement>
                        <RowElement>
                            <Text>{row[OrdersAccessors.id]}</Text>
                        </RowElement>
                        <FlexRowElement>
                            <Text isGray>
                                {formatNumber(row[OrdersAccessors.quantity])}{' '}
                                {t('pages.orders.markingCodes')}
                            </Text>
                        </FlexRowElement>
                    </ListRow>
                )}
                onNextPage={() => pageIncremented()}
                configurationBlock={mapListConfig(
                    configFunc(t),
                    filters,
                    sortings,
                    (value) => filtersChanged([value]),
                    filtersSubmit,
                    filtersReset,
                    sortingsChanged,
                    showConfigBlock,
                )}
                exportConfig={exportConfig}
            />
        </>
    );
};
